import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Checkbox,
  FormControlLabel,
  alpha,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DashboardCustomizeIcon from '@mui/icons-material/DashboardCustomize';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import PaletteIcon from '@mui/icons-material/Palette';
import { getDefaultAppPath, setWhatsNewHidden } from '../utils/whatsNew';
import { neonBlue, shakespeare, kepple, stormGrey } from '../theme/colors';

const FEATURES = [
  {
    title: 'Cleaner UI & UX',
    description: 'Refreshed layout across the dashboard, orders, customers, and admin pages with a modern blue theme, improved tables, and mobile-friendly design.',
    icon: PaletteIcon,
    accent: neonBlue[500],
  },
  {
    title: 'Dashboard statistics & charts',
    description: 'New overview with order activity stats, monthly orders vs deliveries charts, last 10 days trends, and month/year filters to explore past periods.',
    icon: DashboardCustomizeIcon,
    accent: shakespeare[500],
  },
  {
    title: 'Login with phone number',
    description: 'Sign in with your registered phone number (254xxxxxxxxx) and receive a one-time OTP code instead of using a password.',
    icon: PhoneIphoneIcon,
    accent: kepple[500],
  },
];

const FeatureItem = ({ title, description, icon: Icon, accent }) => (
  <Paper
    elevation={0}
    sx={{
      p: 2.5,
      borderRadius: 2,
      border: `1px solid ${alpha(accent, 0.15)}`,
      background: `linear-gradient(135deg, ${alpha(accent, 0.06)} 0%, #ffffff 65%)`,
    }}
  >
    <Stack direction="row" spacing={2} alignItems="flex-start">
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(accent, 0.12),
          color: accent,
          flexShrink: 0,
        }}
      >
        <Icon />
      </Box>
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: stormGrey[900], mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {description}
        </Typography>
      </Box>
    </Stack>
  </Paper>
);

const WhatsNewPage = () => {
  const navigate = useNavigate();
  const [hideOnNextSignIn, setHideOnNextSignIn] = useState(false);
  const userRole = localStorage.getItem('userRole');

  const handleContinue = () => {
    setWhatsNewHidden(hideOnNextSignIn);
    navigate(getDefaultAppPath(userRole), { replace: true });
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: stormGrey[50],
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: { xs: 2, sm: 3 },
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 720,
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${stormGrey[200]}`,
        }}
      >
        <Box
          sx={{
            p: { xs: 2.5, sm: 3 },
            background: `linear-gradient(135deg, ${neonBlue[600]} 0%, ${neonBlue[500]} 50%, ${shakespeare[500]} 100%)`,
            color: '#fff',
          }}
        >
          <Stack direction="row" spacing={1.5} alignItems="center" mb={1}>
            <AutoAwesomeIcon />
            <Typography variant="h4" sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
              What&apos;s New
            </Typography>
          </Stack>
          <Typography variant="body2" sx={{ color: alpha('#fff', 0.9) }}>
            Here&apos;s what&apos;s new in Elite Riders Delivery App
          </Typography>
        </Box>

        <Box sx={{ p: { xs: 2.5, sm: 3 } }}>
          <FormControlLabel
            control={(
              <Checkbox
                checked={hideOnNextSignIn}
                onChange={(event) => setHideOnNextSignIn(event.target.checked)}
                sx={{ '&.Mui-checked': { color: neonBlue[600] } }}
              />
            )}
            label="Don't show this on next sign in"
            sx={{
              mb: 2.5,
              mx: 0,
              alignItems: 'flex-start',
              '& .MuiFormControlLabel-label': {
                color: stormGrey[700],
                fontWeight: 500,
              },
            }}
          />

          <Stack spacing={2} sx={{ mb: 3 }}>
            {FEATURES.map((feature) => (
              <FeatureItem key={feature.title} {...feature} />
            ))}
          </Stack>

          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleContinue}
            sx={{ fontWeight: 600, py: 1.25 }}
          >
            Continue to app
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default WhatsNewPage;
